import { Screen, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonSize, ButtonVariant, ListEmpty } from "@next-platform/client-common-spicy-ui";
import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { useCenterElementDetection } from "@/hooks";
import useCharacterCardGridConfig from "@/hooks/useCharacterCardGridConfig";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { TabNames } from "@/pages/ChatbotMy/types";
import useAuth from "@/services/useAuth";
import { getBusinessRouteScreen, getEmptyStateValue, getNumberOfCols } from "@/utils";
import getShouldBlurNsfwAvatars from "@/utils/getShouldBlurNsfwAvatars";

import { DisplayContext } from "../../types";
import { MyChatbotCard } from "../CharacterCards";
import { useMyChatbotsSearch } from "../contexts/MyChatbotsSearchContext";
import { useCharacterActions, useCharacterData } from "../hooks";
import CharacterGrid from "./CharacterGrid";
import CharacterModals from "./CharacterModals";

interface IMyChatbotsHitsProps {
  nsfwToggle: boolean;
  isPartner: boolean;
  currentCategory: string;
}

const MyChatbotsHits = ({ nsfwToggle, isPartner, currentCategory }: IMyChatbotsHitsProps) => {
  const { t } = useTranslation();
  const refCharacterCardContainer = useRef<HTMLDivElement>(null);
  const { pagePath } = useLocalizedLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isNsfwUserToggleAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.NsfwUserToggle);
  const { userId } = useUserInfo();
  const { likedBots, blurNSFW, chatLanguage, onUserLikebot } = useUserSettings();

  const {
    filteredAndSortedChatbots,
    localSearchQuery: searchQuery,
    localFilterBy: filterBy,
    refetch: onRefresh,
    loadMoreChatbots,
    shouldShowLoadMoreButton,
    isLoadingMoreGroupchats,
  } = useMyChatbotsSearch();

  const customData = filteredAndSortedChatbots;
  const screenName = getBusinessRouteScreen(pagePath) as Screen;

  const {
    characterInfo,
    deleteModalVisible,
    dislikedRecommendedCharacterIds,
    setDeleteModalVisible,
    handleDeleteCharacter,
    confirmDelete: baseConfirmDelete,
    characterCardActionsHandler,
    isDeleteLoading,
  } = useCharacterActions({
    charHits: customData,
    nsfwToggle,
    screenName,
    likedBots,
    onUserLikebot,
  });

  const confirmDelete = async () => {
    await baseConfirmDelete();

    if (onRefresh) {
      await onRefresh();
    }
  };

  const memoizedData = useCharacterData({
    charHits: customData || [],
    likedBots,
    chatLanguage,
    dislikedRecommendedCharacterIds,
    displayContext: DisplayContext.MY_CHATBOTS,
    isPartner,
    blurNSFW: getShouldBlurNsfwAvatars({ isNsfwUserToggleAllowed, blurNSFW }),
    isAuthenticated,
  });

  const { centeredElementIds, setElementRef } = useCenterElementDetection();

  const { colWidth, colGap } = useCharacterCardGridConfig();
  const containerWidth = refCharacterCardContainer.current?.clientWidth || 0;
  const numberOfColumns = useMemo(() => getNumberOfCols({ containerWidth, colWidth, colGap }), [containerWidth, colWidth, colGap]);

  const emptyState = getEmptyStateValue({
    searchedQuery: searchQuery,
    filterBy: filterBy,
    navigate,
    activeTab: TabNames.CHATBOTS,
    t,
  });

  const isDataLoading = !customData;
  const hasNoResult = customData?.length === 0;
  const shouldShowLoading = isDataLoading || hasNoResult;

  return (
    <>
      <div ref={refCharacterCardContainer}>
        {shouldShowLoading ? (
          <div className="grow flex justify-center">
            <ListEmpty
              isLoading={isDataLoading}
              title={emptyState.title}
              description={emptyState.description}
              buttonText={emptyState.buttonText}
              onClick={emptyState.onClick}
            />
          </div>
        ) : (
          <CharacterGrid numberOfColumns={numberOfColumns}>
            {memoizedData.map(
              (dataItem) =>
                !dataItem.isHidden && (
                  <MyChatbotCard
                    key={dataItem.characterId}
                    characterData={dataItem}
                    onAction={(action, characterId) => {
                      void characterCardActionsHandler(action, characterId, currentCategory, userId);
                    }}
                    onDelete={handleDeleteCharacter}
                    hasPermissionToDelete={true}
                    centeredElementIds={centeredElementIds}
                    setElementRef={setElementRef}
                  />
                ),
            )}
          </CharacterGrid>
        )}
      </div>

      {shouldShowLoadMoreButton ? (
        <Row justify="center" className="my-lg">
          <Button
            loading={isLoadingMoreGroupchats}
            onClick={() => void loadMoreChatbots()}
            size={ButtonSize.LARGE}
            variant={ButtonVariant.OUTLINED}
            data-testid="MyChatbotsHits-LoadMoreButton"
          >
            <Trans i18nKey="common:loadMore" />
          </Button>
        </Row>
      ) : null}

      <CharacterModals
        characterInfo={characterInfo}
        deleteModal={{
          visible: deleteModalVisible,
          setVisible: setDeleteModalVisible,
          onConfirm: confirmDelete,
          isLoading: isDeleteLoading,
        }}
      />
    </>
  );
};

export default MyChatbotsHits;
